import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type {
    Commerce,
    CommerceAddress,
    CommerceCheckout,
    CommercePaymentResult,
    SubmitCommercePaymentOrderRequest,
    CommercePaymentState,
} from "@teqtank/commerce-sdk";
import {
    MockCommercePaymentAdapter,
    TeqtankPaymentHandlerAdapter,
} from "@teqtank/commerce-payment-adapter";

@customElement("teqtank-payment")
export class TeqtankPayment extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: false }) public accessor checkout: CommerceCheckout | undefined;
    @property({ attribute: false }) public accessor billingAddress:
        CommerceAddress | null | undefined;
    @property({ attribute: "frame-source" }) public accessor frameSource = "";
    @property({ attribute: false }) public accessor frameUri:
        string | readonly string[] | undefined;
    @property({ attribute: "redirect-loader" }) public accessor redirectLoader =
        "https://tts.teqtank.com/paymentloader";
    @property({ attribute: false }) public accessor paymentStyles:
        Readonly<Record<string, unknown>> | undefined;
    @property({ attribute: "mock-mode", type: Boolean })
    public accessor mockMode = false;
    @property({ attribute: "auto-submit", type: Boolean })
    public accessor autoSubmit = false;
    @property({ attribute: "show-submit", type: Boolean })
    public accessor showSubmit = true;
    @property({ attribute: "hide-error", type: Boolean })
    public accessor hideError = false;
    @property({ attribute: "hide-frame", type: Boolean })
    public accessor hideFrame = false;
    @state() private accessor state: CommercePaymentState | undefined;
    @state() private accessor configuring = true;
    @state() private accessor errorMessage = "";
    private unsubscribe: (() => void) | undefined;
    private adapter: MockCommercePaymentAdapter | TeqtankPaymentHandlerAdapter | undefined;
    private frame: HTMLIFrameElement | null = null;
    private readonly frameId = `teqtank-payment-frame-${++paymentFrameSequence}`;
    private initialUpdate = true;
    private configurationVersion = 0;
    private paymentWindowPrepared = false;

    public static override styles = css`
        :host {
            --tt-payment-frame-background: var(
                --teqtank-payment-frame-background,
                var(
                    --teqtank-checkout-section-background,
                    var(--teqtank-color-background, var(--teqtank-color-surface, #faf8f4))
                )
            );
            container-type: inline-size;
            display: block;
            background: var(--tt-payment-frame-background);
            color: var(--teqtank-color-text, #18221e);
            font-family: Inter, ui-sans-serif, system-ui, sans-serif;
        }

        .payment {
            display: grid;
            gap: 1rem;
        }

        .frame-shell.hidden {
            display: none;
        }

        ::slotted(iframe) {
            display: block;
            border: 0;
        }

        .notice,
        .error,
        .success {
            padding: 1rem;
            border-radius: 0.85rem;
            font-size: 0.88rem;
        }

        .notice {
            background: var(--teqtank-color-surface-soft, #f7f4ee);
            color: var(--teqtank-color-text-muted, #69736d);
        }

        .error {
            background: #fff2ef;
            color: #9b2e1f;
        }

        .success {
            background: #eef8f1;
            color: #23633a;
        }

        .actions {
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 0.75rem;
        }

        button {
            border: 0;
            border-radius: 999px;
            padding: 1rem 1.25rem;
            font: inherit;
            font-weight: 750;
            cursor: pointer;
        }

        .primary {
            background: var(--teqtank-color-primary, #18221e);
            color: #fff;
        }

        .secondary {
            background: transparent;
            color: inherit;
            border: 1px solid var(--teqtank-color-border, #ded8cf);
        }

        button:disabled {
            opacity: 0.55;
            cursor: wait;
        }

        @container (max-width: 560px) {
            .actions {
                grid-template-columns: 1fr;
            }
        }
    `;

    protected override firstUpdated(): void {
        this.ensureFrame();
        void this.configure();
    }

    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (this.initialUpdate) {
            this.initialUpdate = false;
            return;
        }
        if (changed.has("checkout") || changed.has("billingAddress")) this.syncBilling();
        if (
            changed.has("commerce") ||
            changed.has("frameSource") ||
            changed.has("frameUri") ||
            changed.has("redirectLoader") ||
            changed.has("paymentStyles") ||
            changed.has("mockMode")
        ) {
            this.ensureFrame();
            void this.configure();
        }
    }

    public override disconnectedCallback(): void {
        this.configurationVersion += 1;
        this.unsubscribe?.();
        this.unsubscribe = undefined;
        this.closePreparedPaymentWindow();
        this.adapter?.dispose();
        this.adapter = undefined;
        this.frame?.remove();
        this.frame = null;
        super.disconnectedCallback();
    }

    protected override render() {
        const processing = this.state?.status === "processing";
        const succeeded = this.state?.status === "succeeded";

        return html`
            <section class="payment" part="payment">
                ${
                    this.errorMessage && !this.hideError
                        ? html`<div class="error" role="alert">${this.errorMessage}</div>`
                        : nothing
                }
                ${
                    succeeded
                        ? html`<div class="success" role="status">
                              Payment completed successfully. Your order is now being finalised.
                          </div>`
                        : nothing
                }
                ${
                    this.mockMode
                        ? html`
                              <div class="notice">
                                  Demo payment mode is active. No real payment details are
                                  collected.
                              </div>
                          `
                        : html`
                              <div
                                  class="frame-shell ${this.hideFrame ? "hidden" : ""}"
                                  part="frame-shell"
                              >
                                  <slot name="payment-frame"></slot>
                              </div>
                          `
                }
                ${
                    this.showSubmit
                        ? html`<div class="actions">
                              <button
                                  class="primary"
                                  type="button"
                                  ?disabled=${this.configuring || processing || succeeded}
                                  @click=${this.submitFromUserAction}
                              >
                                  ${
                                      processing
                                          ? "Processing secure payment…"
                                          : succeeded
                                            ? "Payment complete"
                                            : "Pay securely"
                                  }
                              </button>
                              ${
                                  processing
                                      ? html`<button
                                            class="secondary"
                                            type="button"
                                            @click=${this.cancel}
                                        >
                                            Cancel
                                        </button>`
                                      : nothing
                              }
                              ${
                                  this.state?.status === "failed"
                                      ? html`<button
                                            class="secondary"
                                            type="button"
                                            @click=${this.submitFromUserAction}
                                        >
                                            Try again
                                        </button>`
                                      : nothing
                              }
                          </div>`
                        : nothing
                }
            </section>
        `;
    }

    private async configure(): Promise<void> {
        const version = ++this.configurationVersion;
        this.errorMessage = "";
        this.configuring = true;
        this.unsubscribe?.();
        this.unsubscribe = undefined;
        try {
            const commerce = this.requireCommerce();
            if (!this.mockMode && this.frameSource.trim().length === 0) {
                this.state = commerce.payment.state;
                return;
            }
            if (!this.mockMode && globalThis.window?.PaymentHandler === undefined) {
                await loadPaymentHandler();
            }
            if (version !== this.configurationVersion || !this.isConnected) return;
            const frame = this.mockMode ? null : this.ensureFrame();
            const frameBackground = this.resolveFrameBackground();
            const paymentStyles = Object.freeze({
                modalBg: frameBackground,
                backgroundColor: frameBackground,
                ...this.paymentStyles,
            });
            const adapter = this.mockMode
                ? new MockCommercePaymentAdapter()
                : new TeqtankPaymentHandlerAdapter({
                      frameId: this.frameId,
                      ...(frame === null ? {} : { frame }),
                      frameUri: this.resolveFrameOrigins(),
                      redirectLoader: this.redirectLoader,
                      styles: paymentStyles,
                  });

            if (version !== this.configurationVersion || !this.isConnected) {
                adapter.dispose();
                return;
            }
            this.adapter = adapter;
            this.state = commerce.payment.configure(adapter);
            this.unsubscribe = commerce.payment.onChanged((event) => {
                this.state = event.state;
            });
            if (frame !== null) frame.src = this.frameSource;
            this.syncBilling();
            if (this.autoSubmit) await this.submitOrder();
        } catch (error) {
            this.errorMessage = this.describe(error);
        } finally {
            if (version === this.configurationVersion) this.configuring = false;
        }
    }

    private ensureFrame(): HTMLIFrameElement | null {
        if (this.mockMode || this.frameSource.trim().length === 0) {
            this.frame?.remove();
            this.frame = null;
            return null;
        }
        if (this.frame !== null) return this.frame;
        const frame = document.createElement("iframe");
        frame.id = this.frameId;
        frame.slot = "payment-frame";
        frame.title = "Secure payment";
        frame.allow = "payment *";
        frame.width = "100%";
        frame.addEventListener("load", () => this.syncBilling());
        this.append(frame);
        this.frame = frame;
        return frame;
    }

    private resolveFrameBackground(): string {
        let candidate: Element | null = this;
        while (candidate !== null) {
            const style = globalThis.getComputedStyle(candidate);
            const color = style.backgroundColor.trim();
            if (color && color !== "transparent" && color !== "rgba(0, 0, 0, 0)") return color;
            const image = style.backgroundImage.trim();
            if (image && image !== "none" && !image.includes("var(")) return image;

            const root = candidate.getRootNode();
            candidate = candidate.parentElement ?? (root instanceof ShadowRoot ? root.host : null);
        }
        return "#faf8f4";
    }

    private resolveFrameOrigins(): string | readonly string[] {
        const configured =
            this.frameUri === undefined
                ? [this.frameSource]
                : typeof this.frameUri === "string"
                  ? [this.frameUri]
                  : this.frameUri;
        const origins = [...new Set(configured.map((value) => new URL(value).origin))];
        if (origins.length === 0)
            throw new Error("At least one allowed payment frame origin is required.");
        return origins.length === 1 ? origins[0]! : Object.freeze(origins);
    }

    public async submit(request: SubmitCommercePaymentOrderRequest = {}): Promise<void> {
        await this.submitOrder(request);
    }

    /**
     * Opens PaymentHandler's redirect loader while browser user activation is still available.
     * Call this synchronously from the final payment CTA when submission continues asynchronously.
     */
    public preparePaymentWindow(): boolean {
        if (this.configuring || this.adapter === undefined) return false;
        if (this.paymentWindowPrepared) return true;
        this.adapter.setPaymentWindow();
        this.paymentWindowPrepared = true;
        return true;
    }

    /** Closes a redirect loader that was prepared but never handed to PaymentHandler.createOrder. */
    public closePreparedPaymentWindow(): void {
        if (!this.paymentWindowPrepared) return;
        this.adapter?.unsetPaymentWindow();
        this.paymentWindowPrepared = false;
    }

    private readonly submitFromUserAction = (): void => {
        if (!this.preparePaymentWindow()) {
            this.errorMessage = "Secure payment is still loading. Try again in a moment.";
            return;
        }
        void this.submitOrder();
    };

    private readonly submitOrder = async (
        request: SubmitCommercePaymentOrderRequest = {},
    ): Promise<void> => {
        this.errorMessage = "";
        try {
            const result = await this.requireCommerce().payment.submitOrder(request);
            this.dispatchCompleted(result);
        } catch (error) {
            this.closePreparedPaymentWindow();
            this.errorMessage = this.describe(error);
            this.dispatchEvent(
                new CustomEvent("teqtank-payment-failed", {
                    detail: { error },
                    bubbles: true,
                    composed: true,
                }),
            );
        }
    };

    private readonly cancel = (): void => {
        this.state = this.requireCommerce().payment.cancel();
        this.dispatchEvent(
            new CustomEvent("teqtank-payment-cancelled", {
                bubbles: true,
                composed: true,
            }),
        );
    };

    private dispatchCompleted(result: CommercePaymentResult): void {
        this.dispatchEvent(
            new CustomEvent("teqtank-payment-complete", {
                detail: { result, checkout: this.checkout },
                bubbles: true,
                composed: true,
            }),
        );
    }

    private requireCommerce(): Commerce {
        if (this.commerce === undefined) throw new Error("Commerce runtime is unavailable.");
        return this.commerce;
    }

    private syncBilling(): void {
        if (this.configuring || this.commerce === undefined) return;
        this.commerce.payment.setBilling(
            this.billingAddress !== undefined
                ? this.billingAddress
                : (this.checkout?.billingAddress ?? null),
        );
    }

    private describe(error: unknown): string {
        return error instanceof Error ? error.message : "Payment could not be completed.";
    }
}

let paymentFrameSequence = 0;
let paymentHandlerLoad: Promise<void> | undefined;

function loadPaymentHandler(): Promise<void> {
    if (globalThis.window?.PaymentHandler !== undefined) return Promise.resolve();
    paymentHandlerLoad ??= new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(
            "script[data-teqtank-payment-handler]",
        );
        if (existing) {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener(
                "error",
                () => reject(new Error("The Teqtank payment library could not be loaded.")),
                { once: true },
            );
            return;
        }
        const script = document.createElement("script");
        script.src = "https://tts.teqtank.com/js/client.api.umd.js";
        script.async = true;
        script.dataset.teqtankPaymentHandler = "true";
        script.addEventListener(
            "load",
            () => {
                if (globalThis.window?.PaymentHandler === undefined) {
                    reject(new Error("The Teqtank payment library loaded without PaymentHandler."));
                    return;
                }
                resolve();
            },
            { once: true },
        );
        script.addEventListener(
            "error",
            () => reject(new Error("The Teqtank payment library could not be loaded.")),
            { once: true },
        );
        document.head.append(script);
    });
    return paymentHandlerLoad;
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-payment": TeqtankPayment;
    }

    interface Window {
        PaymentHandler?: import("@teqtank/commerce-payment-adapter").PaymentHandlerConstructor;
    }
}
